import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {ProcedureSchemaFieldTypeContext} from 'procedures/tables/ProcedureTableConstants';
import {
  fieldOptionToOptionData,
  renderFieldDefinitionOptionContent,
} from 'registry/field/FieldDefinitionInputOrDisplay';
import FieldDefinitionLabel from 'registry/field/FieldDefinitionLabel';
import {getFieldOptions} from 'registry/field/FieldTableOptionGetters';
import {
  FieldOption,
  FieldType,
  isFieldOptionLeafField,
  isFieldOptionSubmenu,
  LeafFieldOption,
} from 'schemas/fields/SchemaFieldConstants';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import {OptionData} from 'ui-library/menus/MenuConstants';
import {getMatchingOptionDatas} from 'ui-library/menus/MenuUtil';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {omit} from 'util/omitKey';

import {TagSchemaSummaryGraphQL} from '../../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';
import {
  DataParameterFieldType,
  DataParameterType,
  MaterialType,
  ProcedureSchemaFieldType,
  SUPPORTED_DATA_PARAMETER_FIELD_TYPES,
} from './procedureSchemaFieldConstants';
import {convertToSchemaField} from './procedureSchemaFieldTypeConversionUtils';

const REMOVE_THESE_OPTIONS: Record<ProcedureSchemaFieldTypeContext, Array<string>> = {
  [ProcedureSchemaFieldTypeContext.DATA_PARAMETER]: [
    'Long text',
    'Integer',
    'Attachment',
    'Entry',
    'Any entity',
    'Custom entity',
    'AA sequence',
    'DNA sequence',
    'Mixture',
    'Molecule',
    'Entity',
  ],
  [ProcedureSchemaFieldTypeContext.MATERIAL_INPUT]: [
    'Long text',
    'Integer',
    'Decimal',
    'Date',
    'Attachment',
    'Entry',
    'Custom entity',
    'AA sequence',
    'DNA sequence',
    'Mixture',
    'Molecule',
    'Schemas',
  ],
  [ProcedureSchemaFieldTypeContext.MATERIAL_OUTPUT]: [
    'Text',
    'Long text',
    'Integer',
    'Decimal',
    'Date',
    'Attachment',
    'Entry',
    'Any entity',
    'Custom entity',
    'AA sequence',
    'DNA sequence',
    'Mixture',
    'Molecule',
    'Schemas',
  ],
  [ProcedureSchemaFieldTypeContext.EQUIPMENT]: [
    'Long text',
    'Integer',
    'Decimal',
    'Date',
    'Attachment',
    'Entry',
    'Custom entity',
    'AA sequence',
    'DNA sequence',
    'Mixture',
    'Molecule',
    'Schemas',
    'Any entity',
  ],
};

function removeUnwantedFieldOptions(
  fieldOption: FieldOption,
  context: ProcedureSchemaFieldTypeContext
): boolean {
  if (typeof fieldOption.label !== 'string') {
    return false;
  }

  // Special case: Filter Date field type but keep Datetime.  Datetime is a
  // submenu under Date, so the Date option still needs to be present for the
  // Datetime option to be accessible.
  if (context === ProcedureSchemaFieldTypeContext.DATA_PARAMETER) {
    if (isFieldOptionLeafField(fieldOption) && fieldOption.fieldDefinition === FieldType.DATE) {
      return false;
    }
  }

  if (REMOVE_THESE_OPTIONS[context].includes(fieldOption.label)) {
    return false;
  }
  return true;
}

/**
 * Note: we opt for a subtractive algorithm here, rather than additive, starting with the default
 * options used in schema configuration. This ensures that if new options are added to schemas, our
 * unit tests will alert us, so we can assess whether to include them here.
 */
function buildFieldOptions(
  fieldOptions: Array<FieldOption>,
  context: ProcedureSchemaFieldTypeContext
): Array<FieldOption> {
  const filteredOptions = fieldOptions
    .filter((fieldOption) => removeUnwantedFieldOptions(fieldOption, context))
    .map((fieldOption) => {
      // Filter unwanted submenu options, and flatten any submenu with a single option into main. (This ensures
      // that the icon from the submenu's container is used, if none is provided on the submenu item.)
      if (isFieldOptionSubmenu(fieldOption)) {
        const filteredSub = fieldOption.submenu.filter((option) =>
          removeUnwantedFieldOptions(option, context)
        );
        if (filteredSub.length === 1) {
          return {...omit(fieldOption, 'submenu'), ...filteredSub[0]};
        }
        return {...fieldOption, submenu: filteredSub};
      }
      return fieldOption;
    });

  // Move everything out of submenus. This specifically ensures named schemas appear at the top level.
  const flattenedOptions: Array<FieldOption> = [];
  for (const option of filteredOptions) {
    if (!isFieldOptionSubmenu(option)) {
      flattenedOptions.push(option);
    } else {
      flattenedOptions.push(...option.submenu);
    }
  }
  return flattenedOptions;
}

function convertToMaterialType(fieldOption: LeafFieldOption): MaterialType {
  const fieldType = fieldOption.fieldDefinition as FieldType.BIOENTITY_LINK | FieldType.TEXT;
  assert([FieldType.BIOENTITY_LINK, FieldType.TEXT].includes(fieldType), 'Misconfigured Material selector');
  return {
    type: fieldType,
    requiredLink: fieldOption.requiredLink,
    unit: null,
  };
}
function convertToDataParameterType(fieldOption: LeafFieldOption): DataParameterType {
  const fieldType = fieldOption.fieldDefinition as DataParameterFieldType;
  assert(
    fieldOption.requiredLink == null && SUPPORTED_DATA_PARAMETER_FIELD_TYPES.includes(fieldType),
    'Misconfigured DataParameter selector'
  );
  return {
    type: fieldType,
    requiredLink: null,
    unit: null,
  };
}

interface SchemaFieldTypeInputOrDisplayProps<T extends ProcedureSchemaFieldType> {
  staticDisplay?: boolean;
  options: Array<FieldOption>;
  selectedType: T | null;
  onChangeSelection: (newType: T) => void;
  convertFromFieldOption: (fieldOption: LeafFieldOption) => T;
}

function SchemaFieldTypeInputOrDisplay<T extends ProcedureSchemaFieldType>(
  props: SchemaFieldTypeInputOrDisplayProps<T>
): JSX.Element {
  const currentFieldState = props.selectedType ? convertToSchemaField(props.selectedType) : undefined;
  const placeholderText = props.staticDisplay ? '' : 'Select a schema';

  const selectedFieldOptionLabel = currentFieldState ? (
    <FieldDefinitionLabel
      field={currentFieldState}
      schemaFieldSelectors={[]}
      ownerUrlId={null}
      inEditMode={true}
    />
  ) : (
    <div>{placeholderText}</div>
  );

  return props.staticDisplay ? (
    selectedFieldOptionLabel
  ) : (
    <SelectDropdown
      displayName={selectedFieldOptionLabel}
      buttonSize={ButtonSize.MINI}
      buttonClassName={css(styles.dropdownButton)}
      menu={
        <SearchableMenu
          getMatchingOptionDatas={(query: string, optionDatas: Array<OptionData>) =>
            getMatchingOptionDatas(query, optionDatas, {
              includeParentOptionNameInSubOptionName: false,
            })
          }
          optionDatas={props.options.map((option) => fieldOptionToOptionData(option))}
          selectedValue={currentFieldState}
          onSelect={(fieldOption) => {
            props.onChangeSelection(props.convertFromFieldOption(fieldOption));
          }}
          renderOptionContent={renderFieldDefinitionOptionContent}
        />
      }
      shouldEllipsize={true}
    />
  );
}

export function ProcedureMaterialTypeSelector(props: {
  staticDisplay?: boolean;
  selectedType: MaterialType | null;
  onChangeSelection: (materialType: MaterialType) => void;
  tagSchemas: Array<TagSchemaSummaryGraphQL> | null;
  context: ProcedureSchemaFieldTypeContext;
}): JSX.Element {
  const fieldOptions = useMemo<null | Array<FieldOption>>(
    () =>
      props.tagSchemas != null
        ? buildFieldOptions(
            getFieldOptions({
              schemaFieldSelectors: [],
              storableSchemas: [],
              tagSchemas: props.tagSchemas,
              schemaInterfaces: [],
            }),
            props.context
          )
        : null,
    [props.context, props.tagSchemas]
  );

  return fieldOptions == null ? (
    <HorizontalThrobber />
  ) : (
    <SchemaFieldTypeInputOrDisplay
      options={fieldOptions}
      {...props}
      convertFromFieldOption={convertToMaterialType}
    />
  );
}

export function ProcedureDataParameterTypeSelector(props: {
  staticDisplay?: boolean;
  selectedType: DataParameterType | null;
  onChangeSelection: (parameterType: DataParameterType) => void;
}): JSX.Element {
  const fieldOptions = useMemo<Array<FieldOption>>(
    () =>
      buildFieldOptions(
        getFieldOptions({
          schemaFieldSelectors: [],
          storableSchemas: [],
          tagSchemas: [],
          schemaInterfaces: [],
        }),
        ProcedureSchemaFieldTypeContext.DATA_PARAMETER
      ),
    []
  );

  return (
    <SchemaFieldTypeInputOrDisplay
      options={fieldOptions}
      {...props}
      convertFromFieldOption={convertToDataParameterType}
    />
  );
}

const styles = StyleSheet.create({
  dropdownButton: {
    minHeight: '16px',
    padding: '2px 12px',
  },
});
